import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  QrCodeScannerOutlined,
  LocationOnOutlined,
  PersonOutlined,
  AutorenewOutlined,
  CheckCircleOutlined,
  ErrorOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { ScanningState } from "../hooks/useScanningFlow";

const ScanStatusBox: React.FC<{
  state: ScanningState;
  expectKeyScanBeforeLocation?: boolean;
}> = ({ state, expectKeyScanBeforeLocation = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  let text = "";
  let detail = "";
  let Icon = QrCodeScannerOutlined;
  let borderColor: string = theme.palette.divider;
  let bgColor = "background.paper";
  let textColor: string = grey[500];
  let isProcessing = false;
  let uppercase = false;

  switch (state.status) {
    case "idle":
      text = expectKeyScanBeforeLocation
        ? t("scanning.prompt.idleKeyFirst")
        : t("scanning.prompt.idle");
      Icon = QrCodeScannerOutlined;
      textColor = grey[500];
      break;
    case "location_selected":
      text = t("scanning.prompt.locationSelected", { name: state.location.name });
      Icon = LocationOnOutlined;
      borderColor = theme.palette.primary.main;
      textColor = theme.palette.primary.main;
      break;
    case "user_selected":
      text = t("scanning.prompt.userSelected", { name: state.user.name });
      Icon = PersonOutlined;
      borderColor = theme.palette.primary.main;
      textColor = theme.palette.primary.main;
      break;
    case "key_scanned":
      text = t("keys.labels.waitingForLocationScan", { barcode: state.lastScannedKeyBarcode });
      Icon = LocationOnOutlined;
      borderColor = theme.palette.primary.main;
      textColor = theme.palette.primary.main;
      break;
    case "task_selected":
      text = t("scanning.prompt.taskSelected", { task: state.task.title });
      Icon = PersonOutlined;
      borderColor = theme.palette.primary.main;
      textColor = theme.palette.primary.main;
      break;
    case "processing":
      text = t("scanning.prompt.processing");
      Icon = AutorenewOutlined;
      textColor = grey[600];
      isProcessing = true;
      break;
    case "success": {
      const isWarning = !!state.warning;
      text = state.message;
      detail = state.warning ?? "";
      Icon = isWarning ? WarningAmberOutlined : CheckCircleOutlined;
      bgColor = isWarning ? theme.palette.warning.main : theme.palette.success.main;
      borderColor = "transparent";
      textColor = isWarning
        ? (theme.palette.warning.contrastText ?? "#fff")
        : (theme.palette.success.contrastText ?? "#fff");
      uppercase = true;
      break;
    }
    case "error":
      text = state.message;
      detail = state.detail ?? "";
      Icon = ErrorOutlined;
      bgColor = theme.palette.error.main;
      borderColor = "transparent";
      textColor = theme.palette.error.contrastText ?? "#fff";
      uppercase = true;
      break;
    default:
      return null;
  }

  return (
    <Box
      data-testid={
        state.status === "success"
          ? "scan-msg-ok"
          : state.status === "error"
            ? "scan-msg-error"
            : undefined
      }
      sx={{
        padding: 2.5,
        border: `1px solid ${borderColor}`,
        borderRadius: 2,
        backgroundColor: bgColor,
        transition: "background-color 0.25s ease, border-color 0.25s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <Icon
          sx={{
            fontSize: 26,
            color: textColor,
            transition: "color 0.25s ease",
            ...(isProcessing && {
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
              animation: "spin 1.5s linear infinite",
            }),
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: textColor,
            fontWeight: uppercase ? 700 : 600,
            transition: "color 0.25s ease",
            ...(uppercase && {
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }),
          }}
        >
          {text}
        </Typography>
      </Box>

      {detail && (
        <Typography
          variant="body2"
          data-testid={state.status === "success" ? "scan-msg-warn" : undefined}
          sx={{
            color: textColor,
            textAlign: "center",
            fontWeight: 600,
            opacity: 0.9,
            marginTop: 1,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
          }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
};

export default ScanStatusBox;
